
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  HiOutlineArrowUpRight,
  HiOutlineArrowLongLeft,
  HiOutlineScale,
} from "react-icons/hi2";

import { getAllServices } from "../redux/actions/services";
import { Loader, ServiceCard } from "../components/utils";

/* ============================================
   SERVICES SECTION - HOME PAGE
============================================ */

function Services() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const { isLoading, Allservices } = useSelector(
    (state) => state.Services || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const content = {
    description: isEnglish
      ? "Comprehensive legal services built around careful analysis, professional representation, and a clear understanding of your legal needs."
      : "خدمات قانونية متكاملة تقوم على الدراسة الدقيقة والتمثيل المهني والفهم الواضح لاحتياجات الأفراد والشركات.",

    empty: isEnglish
      ? "No legal services are available at the moment."
      : "لا توجد خدمات قانونية متاحة حاليًا.",

    discover: isEnglish
      ? "Explore All Services"
      : "استكشف جميع الخدمات",
  };

  /* ======================================
     FETCH SERVICES FROM BACKEND
     Home page: first 6 services
  ====================================== */

  useEffect(() => {
    dispatch(getAllServices(1, 6));
  }, [dispatch]);

  const services = Array.isArray(Allservices)
    ? Allservices.slice(0, 6)
    : [];

  return (
    <section
      id="legal-services"
      aria-labelledby="services-heading"
      className="
        relative isolate
        overflow-hidden
        border-t border-[#D9E5EC]
        bg-[#EDF3F7]
        py-16
        sm:py-20
        lg:py-24

        dark:border-white/10
        dark:bg-[#13293B]

        transition-colors duration-300
      "
    >
      {/* ======================================
          DECORATIVE BACKGROUND
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -end-40 -top-48
          h-[480px] w-[480px]
          rounded-full
          bg-white/40
          blur-3xl

          dark:bg-[#294961]/20
        "
      />

      <div className="container relative z-10">

        {/* ======================================
            SECTION HEADING
        ====================================== */}

        <div
          className="
            mb-12 grid
            items-end gap-7
            lg:mb-14
            lg:grid-cols-[1.2fr_0.8fr]
            lg:gap-14
          "
        >
          {/* Titles */}
          <div className="max-w-[760px]">

            {/* Eyebrow */}
            <div className="mb-5 flex items-center gap-3">
              <span
                aria-hidden="true"
                className="
                  h-px w-10
                  shrink-0
                  bg-skin-accent
                "
              />

              <span className="eyebrow text-skin-accent">
                {t("The practices")}
              </span>
            </div>

            {/* Main title */}
            <h2
              id="services-heading"
              className="
                section-title
                text-skin-heading
              "
            >
              {t("we cover to meet your needs")}
            </h2>

            {/* Small accent */}
            <div
              aria-hidden="true"
              className="
                mt-6 h-[2px] w-14
                rounded-full
                bg-skin-accent/70
              "
            />

          </div>

          {/* Supporting description */}
          <div
            className="
              border-s-2
              border-skin-accent/40
              ps-6
            "
          >
            <p className="section-description text-skin-muted">
              {content.description}
            </p>
          </div>

        </div>

        {/* ======================================
            SERVICES GRID
        ====================================== */}

        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <Loader />
          </div>
        ) : services.length > 0 ? (
          <div
            className="
              grid grid-cols-1
              gap-6

              md:grid-cols-2
              lg:grid-cols-3

              lg:gap-7
            "
          >
            {services.map((service, index) => (
              <ServiceCard
                key={service._id || service.id || index}
                service={service}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div
            className="
              flex min-h-[230px]
              flex-col items-center
              justify-center gap-4
              rounded-2xl
              border border-skin-border
              bg-skin-surface
              p-8 text-center
            "
          >
            <HiOutlineScale
              size={35}
              className="text-skin-accent"
              aria-hidden="true"
            />

            <p className="body-2 text-skin-muted">
              {content.empty}
            </p>
          </div>
        )}

        {/* ======================================
            VIEW ALL SERVICES
        ====================================== */}

        {!isLoading && services.length > 0 && (
          <div
            className="
              mt-12 flex
              justify-center
              lg:mt-14
            "
          >
            <Link
              to="/AllServiceses"
              onClick={() => {
                window.scrollTo(0, 0);
              }}
              className="
                group inline-flex
                min-h-[54px]
                items-center justify-center
                gap-4

                rounded-xl
                border border-brand-navy
                bg-brand-navy
                px-6 py-3

                text-white
                shadow-soft

                transition-all duration-300

                hover:-translate-y-0.5
                hover:bg-brand-navyLight
                hover:shadow-card

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-skin-accent

                dark:border-white/20
                dark:bg-white/10
                dark:text-white
                dark:backdrop-blur-xl
                dark:hover:bg-white/15
              "
            >
              <span className="button-sm">
                {content.discover}
              </span>

              <span
                className="
                  flex h-8 w-8
                  items-center justify-center
                  rounded-lg
                  bg-white/10

                  transition-colors duration-300
                  group-hover:bg-white/20
                "
              >
                <HiOutlineArrowUpRight
                  size={19}
                  aria-hidden="true"
                  className="directional-arrow"
                />
              </span>
            </Link>
          </div>
        )}

        {/* ======================================
            BOTTOM DECORATION
        ====================================== */}

        <div
          aria-hidden="true"
          className="
            mt-12 flex
            items-center justify-center
            gap-3
          "
        >
          <span className="h-px w-10 bg-skin-border" />

          <span
            className="
              h-1.5 w-1.5
              rounded-full
              bg-skin-accent
            "
          />

          <span className="h-px w-10 bg-skin-border" />
        </div>

      </div>
    </section>
  );
}

export default Services;
